import { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Package, Tags } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { PageHelp } from '../../../components/ui/PageHelp';

interface FixedAssetsLayoutProps {
  children: React.ReactNode;
  title?: string;
  description?: string;
  action?: React.ReactNode;
  /** Page-help registry key shown as an info icon next to the title. */
  helpKey?: string;
}

const tabs = [
  { path: '/billing/assets', label: 'Asset List', icon: Package },
  { path: '/billing/assets/categories', label: 'Categories', icon: Tags },
];

/**
 * Shared shell for the Assets section. Keeps the section tabs and the page
 * header consistent across the asset register and its category configuration.
 */
export function FixedAssetsLayout({ children, title, description, action, helpKey }: FixedAssetsLayoutProps) {
  const location = useLocation();

  const activeTabPath = useMemo(() => {
    let best: string | null = null;
    for (const tab of tabs) {
      // `/billing/assets` is the index tab; sub-routes such as
      // `/billing/assets/categories` still activate it via the prefix rule,
      // while the longest match wins so both tabs are never active at once.
      const matches =
        location.pathname === tab.path || location.pathname.startsWith(`${tab.path}/`);
      if (matches && (!best || tab.path.length > best.length)) best = tab.path;
    }
    return best;
  }, [location.pathname]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Package className="w-6 h-6 text-primary" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold text-text">{title || 'Assets'}</h1>
              {helpKey && <PageHelp path={helpKey} />}
            </div>
            {description && <p className="text-sm text-text-muted">{description}</p>}
          </div>
        </div>
        {action && <div className="flex items-center gap-2">{action}</div>}
      </div>

      <div className="bg-surface border border-border rounded-xl p-2 shadow-sm overflow-x-auto">
        <nav className="flex items-center gap-1 min-w-max">
          {tabs.map((tab) => {
            const active = activeTabPath === tab.path;
            return (
              <Link
                key={tab.path}
                to={tab.path}
                className={cn(
                  'flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap',
                  active
                    ? 'bg-primary text-white shadow-sm'
                    : 'text-text-muted hover:text-text hover:bg-background',
                )}
              >
                <tab.icon size={16} />
                {tab.label}
              </Link>
            );
          })}
        </nav>
      </div>

      {children}
    </div>
  );
}
